import { create } from 'zustand';
import type { Profile, ProfileUpdate } from '@/lib/supabase';
import { supabase } from '@/lib/supabase';
import { checkAndHandleAuthError } from '@/lib/authErrorHandler';

export interface ProfileState {
  profile: Profile | null;
  loading: boolean;
  error: string | null;
}

export interface ProfileActions {
  fetchProfile: (userId: string) => Promise<{ error: string | null }>;
  updateProfile: (userId: string, updates: ProfileUpdate) => Promise<{ error: string | null }>;
  createProfile: (userId: string, profileData: Partial<Profile>) => Promise<{ error: string | null }>;
  clearProfile: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setProfile: (profile: Profile | null) => void;
}

export type ProfileStore = ProfileState & ProfileActions;

export const useProfileStore = create<ProfileStore>()((set) => ({
      // Initial state
      profile: null,
      loading: false,
      error: null,

      // Actions
      fetchProfile: async (userId: string) => {
        set({ loading: true, error: null });
        
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

          if (error) {
            // Check if this is an auth error and redirect if needed
            if (checkAndHandleAuthError(error)) {
              set({ error: 'Session expired', loading: false });
              return { error: 'Session expired' };
            }
            set({ error: error.message, loading: false });
            return { error: error.message };
          }

          set({ profile: data, loading: false });
          return { error: null };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch profile';
          set({ error: errorMessage, loading: false });
          return { error: errorMessage };
        }
      },

      updateProfile: async (userId: string, updates: ProfileUpdate) => {
        set({ loading: true, error: null });
        
        try {
          const { data, error } = await supabase
            .from('profiles')
            .update({
              ...updates,
              updated_at: new Date().toISOString(),
            })
            .eq('id', userId)
            .select()
            .single();

          if (error) {
            set({ error: error.message, loading: false });
            return { error: error.message };
          }

          set({ profile: data, loading: false });
          return { error: null };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
          set({ error: errorMessage, loading: false });
          return { error: errorMessage };
        }
      },

      createProfile: async (userId: string, profileData: Partial<Profile>) => {
        set({ loading: true, error: null });
        
        try {
          const now = new Date().toISOString();
          const trialEndDate = new Date();
          trialEndDate.setDate(trialEndDate.getDate() + 14); // 14 days trial

          const defaultProfile = {
            id: userId,
            username: profileData.username || null,
            full_name: profileData.full_name || null,
            avatar_url: profileData.avatar_url || null,
            theme_preference: 'system',
            timer_defaults: {
              focus_duration: 25,
              short_break: 5,
              long_break: 15,
              long_break_interval: 4,
            },
            trial_start_date: now,
            trial_end_date: trialEndDate.toISOString(),
            subscription_status: 'trial',
            subscription_plan: null, // null during trial, set to 'monthly' or 'yearly' when upgrading
            created_at: now,
            updated_at: now,
            ...profileData,
          };

          const { data, error } = await supabase
            .from('profiles')
            .insert(defaultProfile)
            .select()
            .single();

          if (error) {
            set({ error: error.message, loading: false });
            return { error: error.message };
          }

          set({ profile: data, loading: false });
          return { error: null };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to create profile';
          set({ error: errorMessage, loading: false });
          return { error: errorMessage };
        }
      },

      clearProfile: () => set({ profile: null, error: null }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      setProfile: (profile) => set({ profile }),
    }));
